import React from "react";
import {Button} from "antd";
import i18next from "i18next";
import {withRouter} from "react-router-dom";
import ForgetPage from "../ForgetPage";
import * as AuthBackend from "../AuthBackend";
import * as UserBackend from "../../backend/UserBackend";
import * as Obfuscator from "../Obfuscator";
import * as Setting from "../../Setting";
import {SendCodeInput} from "../../common/SendCodeInput";
import LoginThemeScope from "./LoginThemeScope";
import {getLoginTheme} from "./LoginThemes";

// Returns the form tree with getProps(element)'s extra props set on each element, keeping every
// other element as is (a single child stays a single child, which Form.Item relies on)
function patchElements(node, getProps) {
  if (!React.isValidElement(node)) {
    return node;
  }
  const extraProps = getProps(node);
  const children = node.props.children;
  if (children === undefined || typeof children === "function") {
    return extraProps ? React.cloneElement(node, extraProps) : node;
  }
  return React.cloneElement(node, {
    ...extraProps,
    children: Array.isArray(children) ? children.map(child => patchElements(child, getProps)) : patchElements(children, getProps),
  });
}

// ForgetPage itself (ForgetPage.js is untouched), where step 2 sends its code as soon as it shows
// (the email or phone is known from step 1), and with a theme able to:
// - replace the page shell around ForgetPage's step forms (renderForgetPage),
// - show a loading state on the submit buttons while a step is being submitted,
// - show a "password updated" view after a reset instead of redirecting right away.
// The submit handlers below mirror ForgetPage.onFormFinish/onFinish and only add the submitting
// and resetDone states; keep them in step with ForgetPage when upgrading Casdoor.
// Without a theme shell the page renders, and redirects after a reset, exactly as upstream.
class ThemedForgetPageBase extends ForgetPage.WrappedComponent {
  getThemeForgetPage() {
    const application = this.getApplicationObj();
    return application ? getLoginTheme(application).renderForgetPage : undefined;
  }

  trackSubmit(promise) {
    this.setState({submitting: true});
    return promise.finally(() => this.setState({submitting: false}));
  }

  onFormFinish(name, info, forms) {
    switch (name) {
    case "step1":
      this.trackSubmit(AuthBackend.getEmailAndPhone(forms.step1.getFieldValue("organization"), forms.step1.getFieldValue("username"))
        .then((res) => {
          if (res.status !== "ok") {
            Setting.showMessage("error", res.msg);
            return;
          }

          const phone = res.data.phone;
          const email = res.data.email;
          if (!phone && !email) {
            Setting.showMessage("error", i18next.t("general:No verification method"));
            return;
          }

          this.setState({name: res.data.name, phone: phone, email: email});
          const saveFields = (type, dest, fixed) => {
            this.setState({verifyType: type, isVerifyTypeFixed: fixed, dest: dest});
          };
          switch (res.data2) {
          case "email":
            saveFields("email", email, true);
            break;
          case "phone":
            saveFields("phone", phone, true);
            break;
          case "username":
            phone !== "" ? saveFields("phone", phone, false) : saveFields("email", email, false);
          }
          this.setState({current: 1});
        }));
      break;
    case "step2":
      this.trackSubmit(UserBackend.verifyCode({
        application: forms.step2.getFieldValue("application"),
        organization: forms.step2.getFieldValue("organization"),
        username: forms.step2.getFieldValue("dest"),
        name: this.state.name,
        code: forms.step2.getFieldValue("code"),
        type: "login",
      }).then((res) => {
        if (res.status === "ok") {
          this.setState({current: 2, code: forms.step2.getFieldValue("code")});
        } else {
          Setting.showMessage("error", res.msg);
        }
      }));
      break;
    default:
      break;
    }
  }

  onFinish(values) {
    return this.trackSubmit(this.resetPassword(values));
  }

  async resetPassword(values) {
    values.username = this.state.name;
    values.userOwner = this.getApplicationObj()?.organizationObj.name;

    if (this.state.queryParams.get("code")) {
      const res = await UserBackend.verifyCode({
        application: this.getApplicationObj().name,
        organization: values.userOwner,
        username: this.state.queryParams.get("dest"),
        name: this.state.name,
        code: this.state.code,
        type: "login",
      });
      if (res.status !== "ok") {
        Setting.showMessage("error", res.msg);
        return;
      }
    }

    let encryptedNewPassword = values?.newPassword;
    const organization = this.getApplicationObj()?.organizationObj;
    if (organization?.passwordObfuscatorType && organization.passwordObfuscatorType !== "Plain") {
      const [passwordCipher, errorMessage] = Obfuscator.encryptByPasswordObfuscator(organization.passwordObfuscatorType, organization.passwordObfuscatorKey, values?.newPassword);
      if (errorMessage.length > 0) {
        Setting.showMessage("error", errorMessage);
        return;
      }
      encryptedNewPassword = passwordCipher;
    }

    const res = await UserBackend.setPassword(values.userOwner, values.username, "", encryptedNewPassword, this.state.code);
    if (res.status !== "ok") {
      Setting.showMessage("error", res.msg);
      return;
    }

    if (this.getThemeForgetPage()) {
      this.setState({resetDone: true});
    } else {
      this.goToSignin();
    }
  }

  goToSignin() {
    const linkInStorage = sessionStorage.getItem("signinUrl");
    if (linkInStorage !== null && linkInStorage !== "") {
      Setting.goToLinkSoft(this, linkInStorage);
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  renderForm(application) {
    const isThemed = !!this.getThemeForgetPage();
    return patchElements(super.renderForm(application), (element) => {
      if (element.type === SendCodeInput) {
        return {autoSend: true};
      }
      if (isThemed && element.type === Button && element.props.htmlType === "submit") {
        return {loading: !!this.state.submitting};
      }
      return null;
    });
  }

  render() {
    const renderForgetPage = this.getThemeForgetPage();
    return renderForgetPage ? renderForgetPage(this, this.getApplicationObj()) : super.render();
  }
}

const ThemedForgetPageInner = withRouter(ThemedForgetPageBase);

// Drop-in replacement for ForgetPage that applies the application's login theme
function ThemedForgetPage(props) {
  return (
    <LoginThemeScope application={props.application}>
      <ThemedForgetPageInner {...props} />
    </LoginThemeScope>
  );
}

export default ThemedForgetPage;
